// @flow
import React from "react";
import { addQueryParams } from "@nested/utils";

const removeQueryParams = (src) => src.split("?")[0];

const addDefaultParams = (src) =>
  addQueryParams(src, {
    // auto=format,compress allows imgix to convert the image to the best format based on the user's browser and automatically compress
    auto: "format,compress",
    // q sets the output quality of the image, with 100 being the original full quality image. Lower quality => reduced image size
    q: "85",
  });

const createDefaultUrl = (src, { width, height }) => {
  const imageUrl = removeQueryParams(src);
  const params = {
    w: width,
    h: height,
  };

  const srcWithDefaults = addDefaultParams(imageUrl);

  // This adds the params to the URL and ignores any which are undefined
  return Object.keys(params).reduce((url, param) => {
    if (!params[param]) {
      return url;
    }
    return addQueryParams(url, { [param]: params[param] });
  }, srcWithDefaults);
};

const createSrcSet = (src, options) => {
  const url = createDefaultUrl(src, options);
  return `${encodeURI(addQueryParams(url, { dpr: 1 }))}, ${encodeURI(
    addQueryParams(url, {
      // dpr tells imgix about the user's device pixel ratio and allows it to scale up the image for best possible output quality
      dpr: 2,
    }),
  )} 2x`;
};

type ResponsiveImageProps = {
  alt?: string,
  src?: string,
  width?: string,
  height?: string,
  className?: string,
};

export const ResponsiveImage = ({
  alt = "",
  src = "",
  width,
  height,
  className,
}: ResponsiveImageProps) => (
  <img
    className={className}
    alt={alt}
    src={createDefaultUrl(src, { width, height })}
    srcSet={createSrcSet(src, { width, height })}
  />
);
